import produce from 'immer';
import get from 'lodash/get';

import getI13nUtils from 'Lib/i13n/getI13nUtils';
import type { Action } from 'Lib/reduxType';
import isUndefined from 'Lib/utils/isUndefined';

import { getCommonResetAction } from 'Store/commonActions';

import { FETCH_META_SUCCESS } from './constants';

const { executeLogEvent, Log } = getI13nUtils();

/**
 * Creates a reducer that updates state from a value in the processed meta response result
 *
 * This allows the API to configure some app settings via meta.
 *
 * Usage:
 *
 *     // using pure Javascript initialState (recommended)
 *     const yourMetaReducer = createMetaReducer(initialState, 'yourMeta');
 *
 *     // using initialState with a custom `payload.result` path
 *     const yourMetaReducer = createMetaReducer(initialState, 'yourMeta', ['resultPath', 'yourMeta']);
 *
 *     // defining initial state inline
 *     const yourMetaFlagReducer = createMetaReducer(false, ['resultPath', yourMetaFlag']);
 *
 * @param  {Object} initialState - initial state
 * @param  {Array|string} stateKey - key in the state
 * @param  {boolean} useResetAction - reset to initial state on common reset actions (eg. logout)
 * @param  {Array|string} [resultPath] - path to the value in the payload, defaults to stateKey
 * @return {Function} reducer
 */
export const createMetaReducer = (
  initialState: any,
  stateKey: string[] | string,
  useResetAction: boolean = false,
  resultPath: string[] | string = stateKey
) => {
  const payloadPath = ['result'].concat(resultPath);
  const partialState = get(initialState, stateKey);

  return produce<any, [Action]>((draft, action) => {
    switch (action.type) {
      case FETCH_META_SUCCESS: {
        const newState = get(action.payload, payloadPath);
        if (!isUndefined(newState)) {
          return newState;
        }
        // Report any fields that are missing from the meta response to catch any potential issues from v2/meta deprecation
        executeLogEvent(
          new Log({
            message: `Cannot find field "${payloadPath.slice(-1)[0]}" in v2/meta response`,
            customSampleRate: 0.0001,
          })
        );
        break;
      }
      case getCommonResetAction(action.type):
        return useResetAction ? partialState : draft;
      default:
        break;
    }
  }, partialState);
};
